import { useEffect, useRef, useState } from 'react'
import { api } from '../../api/client'
import { useSession } from '../../api/session'

export default function CalendarReconciliation({companyId,attemptId,saved}:{companyId:string;attemptId:string;saved:()=>Promise<void>}) {
  const { user }=useSession()
  const [open,setOpen]=useState(false)
  const [state,setState]=useState<Awaited<ReturnType<typeof api.calendarReconciliation>>|null>(null)
  const [providerId,setProviderId]=useState('')
  const [evidence,setEvidence]=useState('')
  const [error,setError]=useState('')
  const [notice,setNotice]=useState('')
  const [busy,setBusy]=useState(false)
  const [refresh,setRefresh]=useState(0)
  const request=useRef<{body:string;key:string}|null>(null)
  const pending=useRef(false)
  useEffect(()=>{
    if(!open)return
    let live=true
    api.calendarReconciliation(companyId,attemptId).then(value=>{if(live){setState(value);setError('')}}).catch(e=>{if(live)setError(e.message)})
    return()=>{live=false}
  },[companyId,attemptId,open,refresh])
  return <details onToggle={event=>{if(event.target===event.currentTarget)setOpen(event.currentTarget.open)}}>
    <summary>Review calendar email acceptance</summary>
    <p className="muted">Record verified SES acceptance of the archived email below. This does not send or resend an email, or send a new calendar update. Acceptance does not prove delivery or attendance.</p>
    {error && <p className="notice crit" role="alert">{error}</p>}
    {notice && <p role="status">{notice}</p>}
    {!state && open && !error && <p className="muted">Loading archived email and eligibility…</p>}
    {state && <>
      {!state.eligible && <p className="notice">{state.reason || 'This email is not eligible for acceptance reconciliation.'}</p>}
      <p><strong>Archived recipients:</strong> {state.recipients.join(', ') || 'Not available'}</p>
      {state.subject && <p><strong>Archived subject:</strong> {state.subject}</p>}
      {state.body && <details><summary>Review archived email body</summary><pre className="compiled-prompt">{state.body}</pre></details>}
      {state.calendar && <div className="notice">
        <p><strong>Archived calendar:</strong> {state.calendar.method} · revision {state.calendar.sequence}</p>
        <p>Start: {state.calendar.start}<br />End: {state.calendar.end}</p>
        <details><summary>Calendar event identifier</summary><p>{state.calendar.uid}</p></details>
      </div>}
      {state.eligible && user?.role!=='admin' && <p className="muted">An administrator must verify and record email-service acceptance.</p>}
      {state.eligible && user?.role==='admin' && <form className="reply" onSubmit={async event=>{
        event.preventDefault()
        if(pending.current || !providerId.trim() || evidence.trim().length<10)return
        pending.current=true;setBusy(true);setError('');setNotice('')
        try {
          const payload={expected_version:state.version,provider_id:providerId.trim(),evidence:evidence.trim()}
          const body=JSON.stringify(payload)
          if(request.current?.body!==body)request.current={body,key:crypto.randomUUID()}
          await api.reconcileCalendar(companyId,attemptId,{...payload,idempotency_key:request.current.key})
          setState({...state,eligible:false,reason:'SES acceptance has been recorded.'})
          setNotice('Verified SES acceptance recorded. No email was resent.')
          await saved()
        }catch(e){setError(e instanceof Error?e.message:'Could not record verified acceptance')}
        finally{pending.current=false;setBusy(false)}
      }}>
        <label className="field">Verified SES Message ID<input className="input" required maxLength={200} disabled={busy} value={providerId} onChange={event=>setProviderId(event.target.value)} /></label>
        <label className="field">Verification evidence<textarea className="input" required minLength={10} maxLength={5000} rows={4} disabled={busy} value={evidence} onChange={event=>setEvidence(event.target.value)} placeholder="Describe the SES record that verifies acceptance for the archived recipients, email, and calendar details." /></label>
        <button className="btn secondary small" disabled={busy || !providerId.trim() || evidence.trim().length<10}>Record verified SES acceptance</button>
      </form>}
    </>}
    {open && <button className="btn secondary small" style={{marginTop:'.75rem'}} disabled={busy} onClick={()=>{setState(null);setError('');setRefresh(value=>value+1)}}>Reload archived email and eligibility</button>}
  </details>
}
